/**
 * Data layer for `advertiser-notifications`.
 * Thin typed fetch wrappers over /api/v1/advertiser-notifications.
 * Raw `fetch()` is confined to this file per the dash contract (rule D6).
 */

export type NotificationChannel = "email" | "webhook" | "slack";
export type NotificationEventKind = "conversion" | "approval" | "invoice";
export type NotificationEventStatus =
	| "pending"
	| "delivered"
	| "read"
	| "failed";

export interface SubscriptionRow {
	id: string;
	tenant_id: string;
	advertiser_id: string;
	channel: NotificationChannel;
	target: string;
	event_types: string; // csv on disk
	enabled: number; // 0 / 1
	created_at: number;
	updated_at: number;
}

export interface EventRow {
	id: string;
	tenant_id: string;
	advertiser_id: string;
	kind: NotificationEventKind;
	payload: string;
	created_at: number;
	delivered_at: number | null;
	status: NotificationEventStatus;
}

export interface ListSubscriptionsResponse {
	subscriptions: SubscriptionRow[];
}

export interface ListEventsResponse {
	events: EventRow[];
}

export interface CreateSubscriptionBody {
	advertiser_id: string;
	channel: NotificationChannel;
	target: string;
	event_types: NotificationEventKind[] | string;
	enabled?: boolean | 0 | 1;
}

export interface UpdateSubscriptionBody {
	target?: string;
	event_types?: NotificationEventKind[] | string;
	enabled?: boolean | 0 | 1;
}

export interface EmitEventBody {
	advertiser_id: string;
	kind: NotificationEventKind;
	payload: unknown;
}

const BASE = "/api/v1/advertiser-notifications";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listSubscriptions(
	advertiserId: string,
): Promise<ListSubscriptionsResponse> {
	const res = await fetch(
		`${BASE}/subscriptions/${encodeURIComponent(advertiserId)}`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<ListSubscriptionsResponse>(res);
}

export async function createSubscription(
	body: CreateSubscriptionBody,
): Promise<{ subscription: SubscriptionRow }> {
	const res = await fetch(`${BASE}/subscriptions`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<{ subscription: SubscriptionRow }>(res);
}

export async function updateSubscription(
	id: string,
	patch: UpdateSubscriptionBody,
): Promise<{ subscription: SubscriptionRow }> {
	const res = await fetch(
		`${BASE}/subscriptions/${encodeURIComponent(id)}`,
		{
			method: "PATCH",
			credentials: "include",
			headers: {
				accept: "application/json",
				"content-type": "application/json",
			},
			body: JSON.stringify(patch),
		},
	);
	return parseOrThrow<{ subscription: SubscriptionRow }>(res);
}

export async function deleteSubscription(
	id: string,
): Promise<{ ok: boolean; id: string }> {
	const res = await fetch(
		`${BASE}/subscriptions/${encodeURIComponent(id)}`,
		{
			method: "DELETE",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<{ ok: boolean; id: string }>(res);
}

export async function listFeed(
	advertiserId: string,
	limit?: number,
): Promise<ListEventsResponse> {
	const qs =
		typeof limit === "number" && limit > 0
			? `?limit=${encodeURIComponent(String(Math.floor(limit)))}`
			: "";
	const res = await fetch(
		`${BASE}/feed/${encodeURIComponent(advertiserId)}${qs}`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<ListEventsResponse>(res);
}

export async function markEventRead(
	id: string,
): Promise<{ event: EventRow }> {
	const res = await fetch(
		`${BASE}/mark-read/${encodeURIComponent(id)}`,
		{
			method: "POST",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<{ event: EventRow }>(res);
}

export async function emitEvent(
	body: EmitEventBody,
): Promise<{ event: EventRow; delivered_to: number }> {
	const res = await fetch(`${BASE}/emit`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<{ event: EventRow; delivered_to: number }>(res);
}

export const advertiserNotificationsQueryKeys = {
	all: ["advertiser-notifications"] as const,
	subscriptions: (advertiserId: string) =>
		[...advertiserNotificationsQueryKeys.all, "subscriptions", advertiserId] as const,
	feed: (advertiserId: string, limit?: number) =>
		[
			...advertiserNotificationsQueryKeys.all,
			"feed",
			advertiserId,
			limit ?? null,
		] as const,
	detail: (id: string) =>
		[...advertiserNotificationsQueryKeys.all, id] as const,
};
